'use client';

import { AnimatePresence, motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { cn } from '@lib/utils';

import { StepCta } from './step-cta';
import { STEPS } from './steps';

const CTA_CLS =
  'inline-flex h-8 w-fit items-center justify-center rounded-lg bg-[#040435] px-2.5 text-[12px] font-semibold leading-none text-white';

// 모바일: 아코디언. 활성 스텝만 CTA·이미지 노출, 열릴 때 본문이 스르륵 펼쳐진다.
// (컨테이너 flex-col·gap 은 상위 Reveal 이 제공)
export function MobileSteps({
  active,
  setActive,
}: {
  active: number;
  setActive: (i: number) => void;
}) {
  const t = useTranslations('Products.process');

  return (
    <>
      {STEPS.map((step, i) => {
        const isActive = active === i;
        // 스텝 라벨(Step N + 제목) — 활성/비활성 공통
        const label = (
          <span className="flex items-center gap-2">
            <span className="text-sm font-semibold leading-[22px] tracking-[-0.128px] text-[#828A99]">
              Step{i + 1}
            </span>
            <span className="text-base font-semibold leading-6 tracking-[-0.176px] text-[#040435]">
              {t(`steps.${step.key}`)}
            </span>
          </span>
        );
        return (
          <div
            key={step.key}
            className={cn(
              // border 는 항상 깔고 색만 투명↔파랑으로 전환 (검정→파랑 플래시 방지 + 레이아웃 시프트 없음)
              'overflow-hidden rounded-xl border border-solid transition-colors duration-300',
              isActive
                ? 'border-[#D8E9FF] bg-[#F4F8FF]'
                : 'border-transparent bg-[#F7F7F7]',
            )}
          >
            {/* 헤더: 활성 시 CTA, 비활성 시 chevron (버튼 중첩 방지 위해 구조 분리) */}
            {isActive ? (
              <div className="flex h-16 w-full items-center justify-between px-3.5">
                {label}
                <StepCta step={step} className={CTA_CLS} />
              </div>
            ) : (
              <TrackedButton
                type="button"
                label={`process_step-${step.key}_view`}
                position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
                onClick={() => setActive(i)}
                className="flex h-16 w-full items-center justify-between px-3.5"
              >
                {label}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="#828A99"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </TrackedButton>
            )}

            {/* 본문(이미지): height 0 → auto 로 부드럽게 펼쳐짐 */}
            <AnimatePresence initial={false}>
              {isActive && (
                <motion.div
                  key="body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-3.5 pb-4">
                    <div className="relative h-[146px] overflow-hidden rounded-[10px] bg-[#121212]">
                      <Image
                        src={step.img}
                        alt=""
                        fill
                        sizes="100vw"
                        className="object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[rgba(116,174,255,0.4)]" />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );
}
